"use client";

import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";

import { CurrencyIcon, ExpandIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { Price } from "@/components/Price";
import { useCurrency } from "@/hooks/use-currency";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useIsPartnerShare } from "@/hooks/use-is-partner-share";
import { cn } from "@/lib/utils";
import { orpc } from "@/utils/orpc.client";
import type { ReleasePriceStatsResourceValue } from "@garimpa/api/music/getReleasePriceStatsResource";

import { getReleasePriceStatsResourceQueryOptions } from "./price-stats-query";

/** Maps data keys to Grades translation keys (same keys used across the app). */
const CONDITIONS = [
  { key: "priceMint", gradeKey: "Mint" },
  { key: "priceNearMint", gradeKey: "NM" },
  { key: "priceVeryGoodPlus", gradeKey: "VG+" },
  { key: "priceVeryGood", gradeKey: "VG" },
  { key: "priceGoodPlus", gradeKey: "G+" },
  { key: "priceGood", gradeKey: "G" },
  { key: "priceFair", gradeKey: "Fair" },
  { key: "pricePoor", gradeKey: "Poor" },
] as const;

interface PriceStatsProps {
  releaseId: string;
  className?: string;
}

type PriceStatsData = NonNullable<ReleasePriceStatsResourceValue>;
type MarketplacePriceStatsData = Extract<PriceStatsData, { source: "marketplace" }>;
type ConditionPriceStatsData = Exclude<PriceStatsData, { source: "marketplace" }>;

function PriceStatsToggle({
  controls,
  open,
  onClick,
}: {
  controls: string;
  open: boolean;
  onClick: () => void;
}) {
  const t = useTranslations("shared.priceStats");
  return (
    <button
      type="button"
      aria-controls={controls}
      aria-expanded={open}
      onClick={onClick}
      className="flex min-h-11 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <CurrencyIcon className="text-base" decorative />
      {t("marketPrice")}
      <ExpandIcon
        className="text-base transition-transform duration-200"
        style={{ transform: open ? "rotate(180deg)" : undefined }}
        decorative
      />
    </button>
  );
}

function PriceStatsPanelHeader({ compact = false }: { compact?: boolean }) {
  const t = useTranslations("shared.priceStats");
  return (
    <div className={`flex items-center justify-between ${compact ? "mb-2" : "mb-3"}`}>
      <h3 className="text-sm font-semibold">{t("marketPrice")}</h3>
      <span className="text-xs text-muted-foreground">{t("source")}</span>
    </div>
  );
}

function MarketplacePriceStatsPanel({
  className,
  data,
}: {
  className?: string;
  data: MarketplacePriceStatsData;
}) {
  const t = useTranslations("shared.priceStats");
  const [open, setOpen] = useState(false);
  if (!data.lowestPrice && data.numForSale === 0) return null;

  return (
    <div className={className}>
      <PriceStatsToggle
        controls="release-marketplace-price-stats"
        open={open}
        onClick={() => setOpen((value) => !value)}
      />
      <div
        id="release-marketplace-price-stats"
        hidden={!open}
        className="border border-border rounded-lg p-4 mt-2"
      >
        <PriceStatsPanelHeader compact />
        {data.lowestPrice && (
          <p className="text-lg font-bold">
            <Price amount={data.lowestPrice.value} currency={data.lowestPrice.currency} />
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {data.numForSale > 0 ? t("forSale", { count: data.numForSale }) : t("noneForSale")}
        </p>
      </div>
    </div>
  );
}

function conditionPrices(data: ConditionPriceStatsData, label: (key: string) => string) {
  return CONDITIONS.map(({ key, gradeKey }) => ({
    gradeKey,
    label: label(gradeKey),
    value: data[key as keyof ConditionPriceStatsData] as string | null,
  })).filter((price) => price.value != null);
}

function ConditionPriceStatsPanel({
  className,
  data,
}: {
  className?: string;
  data: ConditionPriceStatsData;
}) {
  const t = useTranslations("shared.priceStats");
  const tg = useTranslations("shared.grades");
  const [open, setOpen] = useState(false);
  const prices = conditionPrices(data, (key) => tg(key as Parameters<typeof tg>[0]));
  if (prices.length === 0) return null;

  return (
    <div className={cn("mt-4", className)}>
      <PriceStatsToggle
        controls="release-condition-price-stats"
        open={open}
        onClick={() => setOpen((value) => !value)}
      />
      <div
        id="release-condition-price-stats"
        hidden={!open}
        className="border border-border rounded-lg p-4 mt-2"
      >
        <PriceStatsPanelHeader />
        <div className="space-y-1.5">
          {prices.map(({ gradeKey, label, value }) => (
            <div key={gradeKey} className="flex items-center justify-between gap-4">
              <span className="whitespace-nowrap text-xs text-muted-foreground">{label}</span>
              <span className="whitespace-nowrap text-sm font-medium tabular-nums">
                <Price amount={Number(value)} currency={data.currency} />
              </span>
            </div>
          ))}
        </div>
        {data.source !== "cached" && data.source !== "discogs" && (
          <p className="text-xs text-muted-foreground mt-3">
            <Link to="/settings?tab=connections" className="text-primary hover:underline">
              {t("connectDiscogs")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

function PriceStatsContent({ releaseId, className }: PriceStatsProps) {
  const { currency } = useCurrency();

  const computeReleasePriceStatsResource = useCallback(
    () => orpc.music.computeReleasePriceStatsResource.call({ id: releaseId, currency }),
    [currency, releaseId],
  );
  const { value: data } = useDeferredResourceQuery({
    queryOptions: getReleasePriceStatsResourceQueryOptions(releaseId, currency),
    compute: computeReleasePriceStatsResource,
  });

  if (!data) return null;
  if (data.source === "marketplace") {
    return <MarketplacePriceStatsPanel className={className} data={data} />;
  }
  return <ConditionPriceStatsPanel className={className} data={data} />;
}

export function PriceStats({ releaseId, className }: PriceStatsProps) {
  if (useIsPartnerShare()) return null;
  return <PriceStatsContent releaseId={releaseId} className={className} />;
}
